/**
 * Стили руками, без UI-фреймворка.
 *
 * Решение осознанное: тянуть Tailwind/MUI ради двух панелей и списка — это
 * лишние сотни килобайт в бандл и лишняя зависимость, которую придётся
 * обновлять. Здесь простые inline-объекты; когда экранов станет много,
 * это место превратится в CSS-модули, а не во фреймворк.
 *
 * ЦВЕТА И РАЗМЕРЫ ЗДЕСЬ НЕ ЖИВУТ. Их единственный источник — `theme.ts`,
 * оттуда же питается CSS-строка `roomListCss.ts`. Один источник нужен,
 * чтобы будущая лента легла в ту же систему, а не разъехалась с колонкой.
 */
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

import { color, font, radius, size, space } from "./theme";

/** Старое имя оставлено как алиас: его импортируют четыре компонента. */
export const palette = color;

/**
 * Узкий экран (мобильный): `window.matchMedia("(max-width: 640px)")`.
 *
 * Почему JS, а не CSS @media: вся страница комнаты собрана на inline-стилях
 * (решение этапа «без UI-фреймворка», см. шапку файла), а media-запросы
 * в inline-стилях не выразить. matchMedia даёт то же самое одной строкой
 * и подпиской на изменение. Панель участников и страница комнаты на узком
 * экране становятся во всю ширину: жёсткие 260/360px на телефоне оставляли
 * ленте щель в палец, а сетка медиа и ряд из четырёх кнопок ломались.
 */
export function useNarrowScreen(): boolean {
  const query = "(max-width: 640px)";
  const [narrow, setNarrow] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = () => setNarrow(mql.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);
  return narrow;
}

export const s = {
  app: {
    fontFamily: font.family,
    color: palette.text,
    // Dynamic viewport is critical on iPad Safari: 100vh includes browser
    // chrome/dock space and pushes the composer below the visible screen.
    // dvh follows the actually visible viewport as Safari bars expand/collapse.
    height: "100dvh",
    maxHeight: "100dvh",
    minHeight: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  } as CSSProperties,

  topbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 16px",
    // Верхняя панель — тоже горизонтальная линия, оставляем мягкой.
    borderBottom: `1px solid ${palette.borderSoft}`,
    background: palette.bg,
    flex: "0 0 auto",
  } as CSSProperties,

  title: { fontSize: 15, fontWeight: 600, margin: 0 } as CSSProperties,

  body: { display: "flex", flex: "1 1 auto", minHeight: 0 } as CSSProperties,

  // --- Левая колонка в стиле Telegram ---------------------------------
  // Колонка = шапка (поиск + вкладки) + прокручиваемый список + подвал.
  // Прокручивается ТОЛЬКО список: поиск и вкладки в Telegram всегда на месте.
  // СПИСОК чатов. Рельс спейсов — ОТДЕЛЬНЫЙ элемент слева от этой колонки,
  // а не часть её: у них разный фон и разная прокрутка.
  sidebar: {
    width: size.sidebarWidth,
    flex: `0 0 ${size.sidebarWidth}px`,
    // Шов «список комнат | лента» тоже сильной границей: это край зоны,
    // а не разделитель внутри списка (Антон 16:54 п.1 — рамки отчётливее).
    borderRight: `1px solid ${palette.borderStrong}`,
    background: palette.sidebarBg,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  } as CSSProperties,

  sidebarHead: {
    flex: "0 0 auto",
    borderBottom: `1px solid ${palette.borderSoft}`,
    background: palette.sidebarBg,
  } as CSSProperties,

  searchWrap: {
    position: "relative",
    padding: "8px 10px 6px",
  } as CSSProperties,

  searchIcon: {
    position: "absolute",
    left: 20,
    top: "50%",
    transform: "translateY(-40%)",
    fontSize: 12,
    opacity: 0.5,
    pointerEvents: "none",
  } as CSSProperties,

  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    font: "inherit",
    fontSize: font.small,
    padding: "7px 10px 7px 30px",
    borderRadius: radius.pill,
    border: `1px solid ${color.border}`,
    background: color.bg,
    color: color.text,
  } as CSSProperties,

  // --- Вертикальный рельс пространств (как в Element classic) -------------
  // Узкая колонка слева от списка: Home сверху, ниже круглая иконка на
  // каждый спейс. Рельс и ряд вкладок управляют ОДНИМ состоянием фильтра.
  rail: {
    width: size.railWidth,
    flex: `0 0 ${size.railWidth}px`,
    background: color.railBg,
    // Граница рельса — СИЛЬНАЯ (Антон 16:54 п.2: «левая панель где спейсы
    // недостаточно выделена»). Обычной line здесь мало: рельс, список и
    // лента — три разные зоны, и шов между ними должен быть виден.
    borderRight: `1px solid ${color.borderStrong}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: space.md,
    padding: `${space.md}px 0`,
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
  } as CSSProperties,

  railIconInner: {
    width: size.railIcon,
    height: size.railIcon,
    borderRadius: radius.circle,
    display: "grid",
    placeItems: "center",
    // fontSize действует на ИНИЦИАЛЫ спейсов. У Home внутри SVG-домик
    // (Антон 19:58), и его размер задаётся prop size иконки, а не
    // font-size: растянуть SVG шрифтовым размером нельзя.
    fontSize: font.small,
    fontWeight: font.weightBold,
    color: color.textOnAccent,
    userSelect: "none",
    overflow: "hidden",
    objectFit: "cover",
  } as CSSProperties,

  /** Бейдж непрочитанного поверх иконки спейса. */
  railBadge: {
    position: "absolute",
    right: 2,
    bottom: 0,
    minWidth: 18,
    height: 18,
    padding: "0 4px",
    boxSizing: "border-box",
    borderRadius: 9,
    background: color.tgBlue,
    color: color.textOnAccent,
    fontSize: font.micro,
    fontWeight: font.weightBold,
    lineHeight: "14px",
    textAlign: "center",
    border: `2px solid ${color.railBg}`,
  } as CSSProperties,

  railDivider: {
    width: 24,
    height: 1,
    flex: "0 0 1px",
    background: color.border,
  } as CSSProperties,

  // Горизонтальный ряд вкладок. flexWrap:"nowrap" обязателен явно: без него при 8+
  // вкладках ряд уедет на вторую строку и съест высоту списка.
  tabs: {
    display: "flex",
    flexWrap: "nowrap",
    gap: space.xs,
    padding: "0 6px",
    overflowX: "auto",
    overflowY: "hidden",
  } as CSSProperties,

  // Счётчик внутри вкладки (обязателен в Unread, где есть — там и показываем).
  tabCount: {
    marginLeft: 5,
    padding: "0 5px",
    borderRadius: 9,
    color: color.textOnAccent,
    fontSize: font.micro,
    fontWeight: font.weightHeavy,
    lineHeight: "17px",
    display: "inline-block",
    minWidth: 17,
    textAlign: "center",
    verticalAlign: "text-bottom",
  } as CSSProperties,

  roomScroll: {
    flex: "1 1 auto",
    overflowY: "auto",
    minHeight: 0,
    padding: "4px 4px 8px",
  } as CSSProperties,

  sidebarFoot: {
    flex: "0 0 auto",
    padding: "6px 12px",
    borderTop: `1px solid ${palette.borderSoft}`,
    fontSize: 11,
    color: palette.textDim,
  } as CSSProperties,

  sectionLabel: {
    padding: "8px 10px 4px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: palette.textDim,
  } as CSSProperties,

  sectionDivider: {
    height: 1,
    margin: "6px 10px",
    background: palette.border,
  } as CSSProperties,

  emptyHint: {
    padding: 16,
    fontSize: 13,
    color: palette.textDim,
    lineHeight: 1.5,
  } as CSSProperties,

  avatar: {
    width: size.avatar,
    height: size.avatar,
    borderRadius: radius.circle,
    flex: `0 0 ${size.avatar}px`,
    display: "grid",
    placeItems: "center",
    background: color.sectionBg,
    color: color.sectionText,
    fontSize: 15,
    fontWeight: 600,
    objectFit: "cover",
    overflow: "hidden",
    userSelect: "none",
  } as CSSProperties,

  // Две строки внутри строки чата: имя+время и превью+бейдж.
  // minWidth:0 на контейнере обязателен, иначе flex не даст тексту сжаться
  // и ellipsis никогда не сработает — длинное имя просто вытолкнет время.
  rowTop: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
    minWidth: 0,
  } as CSSProperties,

  rowBottom: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    marginTop: 2,
  } as CSSProperties,

  roomName: {
    flex: "1 1 auto",
    // Список комнат крупнее ленты (Антон 16:54 п.4) — отдельный токен,
    // а не font.body: иначе правка размера сообщений потянет за собой
    // навигацию.
    fontSize: font.roomName,
    // ПРОЧИТАННАЯ комната — ОБЫЧНЫЙ вес (Антон 10.09: «все названия комнат
    // жирные, новые не выделяются»). Здесь стояло 600 — ровно столько же,
    // сколько давал «жирный» для непрочитанного, поэтому выделения не
    // существовало в принципе. Непрочитанное теперь 700, см. RoomList.
    fontWeight: 400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: palette.text,
  } as CSSProperties,

  roomTime: {
    flex: "0 0 auto",
    fontSize: font.tiny,
    color: palette.textDim,
    whiteSpace: "nowrap",
  } as CSSProperties,

  roomPreview: {
    flex: "1 1 auto",
    // Превью следует за именем: если имя выросло до 16, а превью осталось
    // 14, строка выглядит разбалансированной. 15 — на один шаг ниже
    // имени комнаты (16), и этот шаг сохраняется.
    //
    // РАНЬШЕ ЗДЕСЬ БЫЛО НАПИСАНО «15 = размер ленты» — БОЛЬШЕ НЕ ТАК.
    // С 25.09 тело сообщения живёт в `font.messageBody` = 17 (кегль
    // iMessage). Превью ЗА НИМ НЕ ИДЁТ: список — навигация, и превью
    // в 17 стало бы КРУПНЕЕ имени комнаты, т.е. иерархия вверх ногами.
    fontSize: font.body,
    lineHeight: "19px",
    color: palette.textDim,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as CSSProperties,

  previewSender: {
    color: palette.text,
  } as CSSProperties,

  /** Добавка к badge для значка «@»: ровный круг, символ крупнее цифры. */
  mentionBadge: {
    width: 20,
    padding: 0,
    fontWeight: 700,
    marginRight: 4,
  } as CSSProperties,

  badge: {
    flex: "0 0 auto",
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    boxSizing: "border-box",
    borderRadius: 10,
    background: color.tgBlue,
    color: color.textOnAccent,
    fontSize: font.tiny,
    fontWeight: 600,
    lineHeight: "20px",
    textAlign: "center",
  } as CSSProperties,

  main: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    // Лента живёт на ступени feedBg, а не на общем фоне приложения
    // (STEP8 эт.1): область чтения очерчена тёплой ступенью кости,
    // пузыри этапа 2 лягут на неё отдельным фоном (уже есть токены
    // bubbleIn/bubbleOut в theme.ts).
    background: palette.feedBg,
    // Система координат для просмотрщика медиа: он position:absolute и
    // должен ограничиваться ОКНОМ ЧАТА, а не всем браузером (Антон, 10:43).
    position: "relative",
  } as CSSProperties,

  roomHeader: {
    padding: "10px 16px",
    // Шапка чата — горизонтальная линия ВНУТРИ зоны. Антон 16:54(2):
    // «рамки шапки чата оставь как есть». Усиление касалось только
    // вертикальных швов между колонками.
    borderBottom: `1px solid ${palette.borderSoft}`,
    flex: "0 0 auto",
  } as CSSProperties,

  timeline: {
    flex: "1 1 auto",
    overflowY: "auto",
    padding: "12px 16px",
    minHeight: 0,
  } as CSSProperties,

  msgGroupHead: {
    display: "flex",
    gap: 8,
    alignItems: "baseline",
    marginTop: 12,
  } as CSSProperties,

  msgAuthor: { fontSize: font.small, fontWeight: 700 } as CSSProperties,
  msgTime: { fontSize: font.micro, color: palette.textDim } as CSSProperties,
  msgBody: {
    // ТЕЛО СООБЩЕНИЯ — ОТДЕЛЬНЫЙ ТОКЕН, НЕ `font.body`.
    // Антон 25.09 21:26: шрифт как в iMessage «везде, на айфоне
    // критично» — то есть 17. Поднять сам `font.body` было нельзя:
    // на нём сидят кнопки, поля, пункты меню и превью списка
    // комнат — и превью стало бы крупнее имени комнаты (16).
    // Разбор в `theme.ts` → `font.messageBody`.
    fontSize: font.messageBody,
    // Множитель: интервал поднялся вместе с кеглем сам (22.5 → 25.5px),
    // править нечего. Абсолют здесь запрещён — текст слипнется.
    lineHeight: font.lineBody,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    // Длинное слово без пробелов (URL, хеш, id) ломается в любом месте,
    // а не распирает контейнер: wordBreak:"break-word" в части движков
    // не срабатывает внутри flex, поэтому дублируем стандартным
    // overflowWrap. Применяется и в ленте, и в панели треда.
    overflowWrap: "anywhere",
  } as CSSProperties,

  // --- Пузыри сообщений (STEP8 эт.2) --------------------------------------
  // Пузырь — контейнер сообщения. Свой справа (bubbleOut, градиент),
  // чужой слева (bubbleIn). Градиент — требование постановки Антона
  // 16:21: «свой пузырь — тёплый оттенок с лёгким градиентом в сторону
  // красного». Чужой пузырь — без градиента, просто своя ступень кости.
  // Оба обязаны отличаться от ленты feedBg (постановка).

  /** Контейнер одного сообщения в ленте. */
  bubbleRow: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "min(420px, 78%)",
    // Отступы у ряда пузыря НУЛЕВЫЕ: расстояние между сообщениями задаёт
    // Timeline по позиции в группе. Симметричные marginTop/Bottom здесь
    // разводили подряд идущие сообщения одного автора на 8px, и группа
    // не слипалась — ровно то, за что Антон назвал пузыри недоделанными.
    marginTop: 0,
    marginBottom: 0,
  } as CSSProperties,

  /** Чужое сообщение (входящее) — слева, своя ступень кости. */
  bubbleIn: {
    alignSelf: "flex-start",
    background: color.bubbleIn,
    borderRadius: radius.bubble,
    padding: `${space.md}px ${space.lg}px`,
    color: color.text,
    position: "relative",
  } as CSSProperties,

  /** Своё сообщение (исходящее) — справа, тёплый градиент в сторону красного. */
  bubbleOut: {
    alignSelf: "flex-end",
    background: `linear-gradient(180deg, ${color.bubbleOut} 0%, ${color.bubbleOutTo} 100%)`,
    borderRadius: radius.bubble,
    padding: `${space.md}px ${space.lg}px`,
    color: color.text,
    position: "relative",
  } as CSSProperties,

  /** Хвостик пузыря у последнего сообщения в группе (как в Telegram).
      Маленький треугольник внизу, прижатый к краю. Реализован через ::after
      в CSS-строке: inline-стиль не умеет псевдоэлементы, а отдельный div
      ломал бы выравнивание текста. */
  bubbleTailIn: {
    borderBottomLeftRadius: 4,
  } as CSSProperties,

  bubbleTailOut: {
    borderBottomRightRadius: 4,
  } as CSSProperties,

  /** Время внутри пузыря — справа снизу, мелким шрифтом. */
  bubbleTime: {
    fontSize: font.tiny,
    color: palette.textDim,
    float: "right",
    marginLeft: space.md,
    marginTop: space.xs,
    lineHeight: 1,
    userSelect: "none",
  } as CSSProperties,

  /** Выделение найденного сообщения (переход по ссылке, поиск). */
  bubbleHighlight: {
    background: color.highlight,
    transition: "background 1.5s ease-out",
  } as CSSProperties,

  /** Обёртка низа чата: отбивка от ленты. Сама строка — .alg-composer-bar
      в composerCss (постановка Антона 19:11 «узкой красивой строкой»).
      Здесь только внешние поля: раньше тут же были gap и alignItems для
      кнопок, стоявших рядом с полем, — из-за них низ был высоким. */
  composerOuter: {
    padding: "8px 12px 10px",
    borderTop: `1px solid ${palette.borderSoft}`,
    flex: "0 0 auto",
  } as CSSProperties,

  composer: {
    display: "flex",
    gap: 8,
    padding: 12,
    borderTop: `1px solid ${palette.borderSoft}`,
    flex: "0 0 auto",
    alignItems: "flex-end",
  } as CSSProperties,

  textarea: {
    flex: "1 1 auto",
    resize: "none",
    font: "inherit",
    fontSize: font.body,
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${palette.border}`,
    maxHeight: 160,
    minHeight: 38,
    lineHeight: 1.4,
  } as CSSProperties,

  button: {
    font: "inherit",
    fontSize: font.body,
    padding: "9px 16px",
    borderRadius: 8,
    border: "none",
    background: palette.accent,
    color: "#fff",
    cursor: "pointer",
  } as CSSProperties,

  notice: {
    margin: 16,
    padding: "12px 14px",
    borderRadius: 8,
    background: palette.warnBg,
    border: `1px solid ${palette.warnBorder}`,
    fontSize: 13,
    lineHeight: 1.5,
  } as CSSProperties,

  centered: {
    display: "grid",
    placeItems: "center",
    height: "100%",
    color: palette.textDim,
    fontSize: 14,
    padding: 24,
    textAlign: "center",
  } as CSSProperties,

  /** Подпись с текущим сервером на экране входа (прод/стенд). */
  serverNote: {
    marginTop: 18,
    fontSize: 12,
    color: palette.textDim,
  } as CSSProperties,

  /** «сменить» рядом с названием сервера: ссылка, а не кнопка — действие вторичное. */
  serverSwitch: {
    marginLeft: 8,
    background: "none",
    border: "none",
    padding: 0,
    color: palette.accent,
    font: "inherit",
    fontSize: 12,
    textDecoration: "underline",
    cursor: "pointer",
  } as CSSProperties,

  // --- Управление комнатами (этап «базовые функции») ---------------------
  // Всё ниже — намеренно ЧЕРНОВОЙ вид: прямоугольники, рамка, отступы.
  // Задача этапа — работающие функции, а не пиксели (решение Антона 19:07).

  /** Затемнение под модальным окном. */
  dialogBackdrop: {
    position: "fixed",
    inset: 0,
    background: color.backdrop,
    display: "grid",
    placeItems: "center",
    zIndex: 50,
  } as CSSProperties,

  dialogBox: {
    background: color.bg,
    borderRadius: radius.md,
    border: `1px solid ${color.border}`,
    padding: space.xl,
    width: 380,
    maxWidth: "90vw",
    display: "flex",
    flexDirection: "column",
    gap: space.lg,
  } as CSSProperties,

  dialogTitle: {
    fontSize: 15,
    fontWeight: font.weightBold,
    color: color.text,
  } as CSSProperties,

  dialogInput: {
    width: "100%",
    boxSizing: "border-box",
    font: "inherit",
    fontSize: font.body,
    marginTop: space.sm,
    padding: "7px 10px",
    borderRadius: radius.sm,
    border: `1px solid ${color.border}`,
    background: color.bg,
    color: color.text,
  } as CSSProperties,

  dialogError: {
    fontSize: font.small,
    color: color.danger,
    lineHeight: 1.4,
    wordBreak: "break-word",
  } as CSSProperties,

  dialogFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: space.md,
  } as CSSProperties,

  /** Контекстное меню строки чата (правый клик). Позиция задаётся инлайн. */
  menu: {
    position: "fixed",
    background: color.bg,
    border: `1px solid ${color.border}`,
    borderRadius: radius.sm,
    padding: space.sm,
    minWidth: 200,
    zIndex: 60,
    display: "flex",
    flexDirection: "column",
  } as CSSProperties,

  menuItem: {
    font: "inherit",
    fontSize: font.small,
    textAlign: "left",
    padding: "7px 10px",
    border: "none",
    background: "transparent",
    color: color.text,
    cursor: "pointer",
    borderRadius: radius.sm,
    whiteSpace: "nowrap",
  } as CSSProperties,

  /** Ряд кнопок действий в шапке колонки и в шапке комнаты. */
  actionRow: {
    display: "flex",
    gap: space.sm,
    alignItems: "center",
    flexWrap: "wrap",
  } as CSSProperties,

  actionButton: {
    font: "inherit",
    fontSize: font.micro,
    padding: "4px 8px",
    borderRadius: radius.sm,
    border: `1px solid ${color.border}`,
    background: color.bg,
    color: color.text,
    cursor: "pointer",
    whiteSpace: "nowrap",
  } as CSSProperties,

  /** Полоса ошибки действия под верхней панелью. */
  actionError: {
    flex: "0 0 auto",
    padding: "8px 16px",
    background: color.warnBg,
    borderBottom: `1px solid ${color.warnBorder}`,
    fontSize: font.small,
    color: color.danger,
    display: "flex",
    alignItems: "center",
  } as CSSProperties,

  /** Кнопки «Принять/Отклонить» прямо в строке приглашения. */
  inviteBar: {
    display: "flex",
    gap: space.md,
    padding: `0 ${space.lg}px ${space.md}px`,
  } as CSSProperties,

  /** Правая панель со списком участников. */
  memberPanel: {
    width: 260,
    flex: "0 0 260px",
    // Вертикальный шов между зонами (лента | правая панель) — сильной
    // границей, как рельс и список слева (Антон 16:54(2): поправить
    // вертикальные рамки).
    borderLeft: `1px solid ${color.borderStrong}`,
    background: color.sidebarBg,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  } as CSSProperties,

  /** Правая панель треда: чуть уже панели участников, чтобы не съедать
      основную ленту целиком. */
  threadPanel: {
    width: 340,
    flex: "0 0 340px",
    // Вертикальный шов между зонами (лента | правая панель) — сильной
    // границей, как рельс и список слева (Антон 16:54(2): поправить
    // вертикальные рамки).
    borderLeft: `1px solid ${color.borderStrong}`,
    background: color.sidebarBg,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  } as CSSProperties,

  /** Страница комнаты (как в Telegram Desktop): прокручиваемая панель справа. */
  roomPage: {
    width: 360,
    flex: "0 0 360px",
    // Вертикальный шов между зонами (лента | правая панель) — сильной
    // границей, как рельс и список слева (Антон 16:54(2): поправить
    // вертикальные рамки).
    borderLeft: `1px solid ${color.borderStrong}`,
    background: color.sidebarBg,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    // Мобильная адаптация: на узких экранах панель не должна ломать сетку.
    // 360px — комфортный минимум для четырёх кнопок и сетки по три.
    maxWidth: "100vw",
    boxSizing: "border-box",
  } as CSSProperties,

  /** Строка-пункт страницы комнаты: иконка + текст, вся строка кликабельна. */
  roomPageRow: {
    display: "flex",
    alignItems: "center",
    gap: space.lg,
    width: "100%",
    boxSizing: "border-box",
    padding: `10px ${space.xl}px`,
    border: "none",
    background: "transparent",
    font: "inherit",
    fontSize: font.body,
    color: color.text,
    textAlign: "left",
    cursor: "pointer",
  } as CSSProperties,

  /** Компактная иконка-кнопка в шапке комнаты (уведомления, «ещё»). */
  iconButton: {
    font: "inherit",
    fontSize: 15,
    lineHeight: 1,
    width: 32,
    height: 32,
    flex: "0 0 32px",
    borderRadius: radius.circle,
    border: "none",
    background: "transparent",
    color: color.textDim,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  } as CSSProperties,

  memberRow: {
    display: "flex",
    alignItems: "center",
    gap: space.md,
    padding: `${space.md}px ${space.lg}px`,
    // Разделитель между строками начинается ПОСЛЕ аватара, а не во всю
    // ширину панели (образец Антона 10.09): линия идёт от текста, аватар
    // стоит на чистом фоне. Реализовано градиентным borderImage:
    // прозрачные 68px слева + линия дальше
    // (68 = padding-left 12 + аватар 48 + gap 8).
    // У ПЕРВОЙ строки списка линия гасится инлайном (borderTop: "none") —
    // см. MemberPanel: разделитель нужен МЕЖДУ строками, а не над первой.
    borderTop: "1px solid transparent",
    borderImage: `linear-gradient(to right, transparent 68px, ${color.border} 68px) 1`,
  } as CSSProperties,

  memberAvatar: {
    // Аватар 48px вместо 32 (образец Антона 10.09): строка участника стала
    // выше, имя и статус читаются по отдельности, а не сливаются в серую
    // массу плотного списка ссылок.
    width: 48,
    height: 48,
    flex: "0 0 48px",
    borderRadius: radius.circle,
    display: "grid",
    placeItems: "center",
    fontSize: font.body,
    fontWeight: font.weightBold,
    color: color.textOnAccent,
    objectFit: "cover",
    overflow: "hidden",
    userSelect: "none",
  } as CSSProperties,
} as const;
